import type { Snapshot } from '@ember-data/legacy-compat/-private';
import { EmbeddedRecordsMixin } from '@ember-data/serializer/rest';
import type Store from '@ember-data/store';
import type { ModelSchema } from '@ember-data/store/types';
import type { CurrencyConversionExchangeRatesProfile } from 'lumen/models';
import ApplicationSerializer from 'lumen/serializers/application';

// eslint-disable-next-line @typescript-eslint/no-unsafe-call
export default class CurrencyConversionExchangeRatesProfileSerializer extends ApplicationSerializer.extend(
  EmbeddedRecordsMixin,
) {
  attrs = {
    rates: { embedded: 'always' },
    createdAt: { serialize: false },
    updatedAt: { serialize: false },
    baseCurrency: { serialize: false },
  };

  modelNameFromPayloadKey(key: string): string {
    if (key === 'profile' || key === 'profiles') {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      return super.modelNameFromPayloadKey(
        'currency-conversion/exchange-rates-profile',
      );
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    return super.modelNameFromPayloadKey(key);
  }

  normalizeCreateRecordResponse(
    store: Store,
    primaryModelClass: ModelSchema,
    payload: Record<string, unknown>,
    id: string | number,
    requestType: string,
  ): Record<string, unknown> {
    /**
     * The API returns data under a 'profile' key.
     * We need to extract it and wrap it with the model name for Ember Data.
     */
    const restPayload = {
      'currency-conversion/exchange-rates-profile': payload['profile'],
    };

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    return super.normalizeCreateRecordResponse(
      store,
      primaryModelClass,
      restPayload,
      id,
      requestType,
    );
  }

  normalizeUpdateRecordResponse(
    store: Store,
    primaryModelClass: ModelSchema,
    payload: Record<string, unknown>,
    id: string | number,
    requestType: string,
  ): Record<string, unknown> {
    /**
     * The API returns data under a 'profile' key on update responses.
     * We need to extract it and wrap it with the model name for Ember Data.
     * This ensures embedded rates are properly updated in the store.
     *
     * Unload unsaved (isNew) rate records that are included in the response before
     * normalizing. The server assigns IDs to these records, and without unloading them
     * first, Ember Data would duplicate them (one local without ID, one from server with ID).
     * Rate records not in the response (e.g. title-only saves) are left untouched.
     */

    const existingProfile =
      store.peekRecord<CurrencyConversionExchangeRatesProfile>(
        'currency-conversion/exchange-rates-profile',
        id,
      );

    if (existingProfile) {
      const responseRates =
        ((payload['profile'] as Record<string, unknown>)?.['rates'] as {
          currency_code: string;
        }[]) ?? [];
      const responseCurrencyCodes = new Set(
        responseRates.map((r) => r.currency_code),
      );

      // Only unload isNew rates that the API returned with an ID in this response.
      // Rates absent from the response were not sent (e.g. stripped because they
      // were invalid) — preserve them so in-progress edits are not discarded.
      const unsavedRates = existingProfile.rates?.filter(
        (rate: { currencyCode: string; isNew: boolean }) =>
          rate.isNew && responseCurrencyCodes.has(rate.currencyCode),
      );

      if (unsavedRates) {
        unsavedRates.forEach((rate: { unloadRecord: () => void }) => {
          rate.unloadRecord();
        });
      }
    }

    const restPayload = {
      'currency-conversion/exchange-rates-profile': payload['profile'],
    };

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    return super.normalizeUpdateRecordResponse(
      store,
      primaryModelClass,
      restPayload,
      id,
      requestType,
    );
  }

  payloadKeyFromModelName(): string {
    return 'profile';
  }

  serialize(
    snapshot: Snapshot<CurrencyConversionExchangeRatesProfile>,
    options: object,
  ): Record<string, unknown> {
    if (snapshot.isNew) {
      return {
        profile: {
          title: snapshot.record?.title ?? '',
          base_currency: snapshot.record?.baseCurrency?.id ?? '',
        },
      };
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    const json = super.serialize(snapshot, options);

    // Defense-in-depth: strip the rates array when there are no valid rates,
    // to avoid API validation errors. In practice, title updates omit rates by
    // default (adapter opt-in), and the Save button is disabled when hasInvalidRates.
    if (
      snapshot.record?.hasNoExchangeRates &&
      snapshot.record?.hasInvalidRates
    ) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      delete json.rates;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return json;
  }

  serializeIntoHash(
    hash: Record<string, unknown>,
    _ModelClass: ModelSchema,
    snapshot: Snapshot<CurrencyConversionExchangeRatesProfile>,
    options: object,
  ): void {
    /**
     * By default, RESTSerializer uses the model name as the root key.
     * For this serializer, we want to avoid having a root key entirely.
     */
    Object.assign(hash, this.serialize(snapshot, options));
  }
}
